import { AFFILIATION_AUDIENCES } from "@ondo/shared";
import type { AffiliationAudience } from "@ondo/shared";
import { useId, useState } from "react";

import { Tag } from "../lib/Tag.tsx";
import { Panel } from "./ProfilePanels.tsx";
import {
  AFFILIATION_AUDIENCE_LABELS,
  AFFILIATION_AUDIENCE_NOTES,
  PROFILE_COPY,
} from "./profile.constants.ts";
import type { AffiliationRow } from "./profile.model.ts";

/**
 * Who you have said you are with, and what they answered.
 *
 * The inbound half of this already exists on the Verifications screen; this is
 * the half nobody had built, though `claimAffiliation`, `withdrawAffiliationClaim`
 * and `fetchAffiliationClaimsMade` have been sitting in `lib/api.ts` unused.
 *
 * A CONFIRMED CLAIM GRANTS NOTHING — the first line of the migration, repeated
 * here because this is the screen where somebody would assume otherwise. No
 * membership is written, no workspace opens, no read widens. It is a fact
 * somebody was willing to stand behind, which is what the whole registry trades
 * in, and no more.
 *
 * A status is a word, never a tally. `accessFGARules.md` is explicit that
 * nothing here is derived from counting, and two answers that disagree is a real
 * situation in this market that arithmetic cannot resolve.
 *
 * CONFIRMING AND SHOWING ARE TWO CONTROLS, AND THEY ARE NOT EVEN NEXT TO EACH
 * OTHER. OND-98's first decision is that they are separate choices, and the
 * answer is not on this screen at all — the other party gives it, in their own
 * inbox. What is here is the audience, which belongs to whoever made the claim,
 * and the answer rendered beside it as a fact that cannot be edited from this
 * side.
 *
 * THE ANSWER IS DRAWN FROM `row.answer` AND `row.status` TOGETHER, and the view
 * has no branch that could print one without the other. A relationship whose
 * status read "Confirmed" while a dispute sat unrendered would be this screen
 * telling a lie on the registry's behalf, which is a worse failure than any
 * leak in this panel — so the decision about what a standing may be called is
 * in `@ondo/shared`, and this file only prints what it is handed.
 */
export function ProfileAffiliations({
  rows,
  busy,
  error,
  onAdd,
  onWithdraw,
  onSetAudience,
}: {
  rows: AffiliationRow[];
  busy: boolean;
  /**
   * The refusal from the last write, which this panel had no way to show.
   *
   * `actionError` reached `ProfileContact` alone, and that renders it only
   * while its own draft is open — so a malformed id typed here produced no
   * request and no sentence anywhere on the screen.
   */
  error: string | null;
  onAdd: (input: { ondoId: string; title: string; note: string }) => string | null;
  onWithdraw: (claimId: string) => void;
  /** One audience, ticked or unticked. The controller composes the other two. */
  onSetAudience: (claimId: string, audience: AffiliationAudience, enabled: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const [ondoId, setOndoId] = useState("");
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");

  const idField = useId();
  const titleField = useId();
  const noteField = useId();

  return (
    <Panel
      title={PROFILE_COPY.affiliationsHeading}
      note={PROFILE_COPY.affiliationsNote}
      action={
        <button
          type="button"
          className="profile__action"
          onClick={() => setOpen((value) => !value)}
        >
          {open ? PROFILE_COPY.cancel : PROFILE_COPY.affiliationAdd}
        </button>
      }
    >
      {rows.length === 0 && !open && (
        <p className="profile__empty">{PROFILE_COPY.affiliationsEmpty}</p>
      )}

      {rows.length > 0 && (
        <>
          <p className="profile__panel-note">{PROFILE_COPY.affiliationVisibilityNote}</p>
          {/*
            The sentence this panel exists to not get wrong. A reader who has
            just watched a gallery confirm that they work there is one
            assumption away from believing the confirmation lets them act on
            that gallery's works, and nothing else on the screen contradicts it.
          */}
          <p className="profile__panel-note">{PROFILE_COPY.affiliationNoAuthority}</p>
        </>
      )}

      {rows.length > 0 && (
        <ul className="profile__rows">
          {rows.map((row) => (
            <li className="profile__row profile__row--stacked" key={row.claimId}>
              <div className="profile__row-head">
                <div className="profile__row-body">
                  <span className="profile__row-title">{row.subject}</span>
                  {row.title && <span className="profile__row-meta">{row.title}</span>}
                  {row.claimedFor && (
                    <span className="profile__row-meta">{row.claimedFor}</span>
                  )}
                </div>
                <Tag tone={row.tone}>{row.status}</Tag>
                {row.canWithdraw && (
                  <button
                    type="button"
                    className="profile__action"
                    disabled={busy}
                    onClick={() => onWithdraw(row.claimId)}
                  >
                    {PROFILE_COPY.affiliationWithdraw}
                  </button>
                )}
              </div>

              {row.answer && (
                <blockquote className="profile__answer">
                  <span className="profile__label">
                    {PROFILE_COPY.affiliationAnswerPrefix}
                  </span>
                  <p className="profile__answer-body">{row.answer.body}</p>
                  {row.answer.by && (
                    <span className="profile__row-meta">
                      {PROFILE_COPY.affiliationAnsweredBy} {row.answer.by}
                    </span>
                  )}
                </blockquote>
              )}

              {row.audiences && (
                <fieldset className="profile__audience" disabled={!row.canPublish || busy}>
                  <legend className="profile__label">
                    {PROFILE_COPY.affiliationVisibility}
                  </legend>
                  {AFFILIATION_AUDIENCES.map((audience) => (
                    <label className="profile__audience-choice" key={audience}>
                      <input
                        type="checkbox"
                        checked={row.audiences?.[audience] ?? false}
                        onChange={(event) =>
                          onSetAudience(row.claimId, audience, event.target.checked)
                        }
                      />
                      <span className="profile__audience-label">
                        {AFFILIATION_AUDIENCE_LABELS[audience]}
                      </span>
                      <span className="profile__audience-note">
                        {AFFILIATION_AUDIENCE_NOTES[audience]}
                      </span>
                    </label>
                  ))}
                  <p className="profile__row-meta">{row.visibility}</p>
                </fieldset>
              )}
            </li>
          ))}
        </ul>
      )}

      {open && (
        <form
          className="profile__form"
          onSubmit={(event) => {
            event.preventDefault();
            // Cleared only once the claim has gone. A refused id keeps all
            // three fields, so the correction is a keystroke rather than a
            // retype — the rule every other form on this screen follows.
            if (onAdd({ ondoId, title, note }) !== null) return;
            setOndoId("");
            setTitle("");
            setNote("");
          }}
        >
          <label className="profile__label" htmlFor={idField}>
            {PROFILE_COPY.affiliationIdLabel}
          </label>
          <input
            id={idField}
            className="profile__input"
            value={ondoId}
            placeholder="ONDO-6624"
            onChange={(event) => setOndoId(event.target.value)}
          />

          <label className="profile__label" htmlFor={titleField}>
            {PROFILE_COPY.affiliationTitleLabel}
          </label>
          <input
            id={titleField}
            className="profile__input"
            value={title}
            maxLength={200}
            onChange={(event) => setTitle(event.target.value)}
          />

          <label className="profile__label" htmlFor={noteField}>
            {PROFILE_COPY.affiliationNoteLabel}
          </label>
          <textarea
            id={noteField}
            className="profile__input profile__input--area"
            value={note}
            maxLength={2000}
            rows={3}
            onChange={(event) => setNote(event.target.value)}
          />

          {error && (
            <p className="profile__error" role="alert">
              {error}
            </p>
          )}

          <div className="profile__form-actions">
            <button type="submit" className="profile__primary" disabled={busy}>
              {PROFILE_COPY.affiliationSubmit}
            </button>
          </div>
        </form>
      )}
    </Panel>
  );
}
